import {
  DailyQuestKey,
  OnboardingStepKey,
  ShopRewardType,
  WeeklyQuestKey,
} from "../types/gameMeta"

export const ONBOARDING_STEP_DEFAULTS: Record<
  OnboardingStepKey,
  { title: string; description: string }
> = {
  LEAGUE_SELECTED: {
    title: "Выбери лигу",
    description: "Создай игровой профиль и выбери путь Титана или Атланта",
  },
  ACTIVE_CARD_SET: {
    title: "Активная карта",
    description: "Выбери карту для отображения в профиле",
  },
  DAILY_QUIZ_DONE: {
    title: "Первый тест",
    description: "Пройди ежедневный тест в Академии — засчитывается после отправки ответов",
  },
  LOCATION_SCANNED: {
    title: "Первый скан",
    description: "Отсканируй точку на карте города",
  },
  BATTLE_PLAYED: {
    title: "Первая дуэль",
    description: "Сыграй тренировку или бой через RADAR / 1 на 1",
  },
}

/** @deprecated используй ONBOARDING_STEP_DEFAULTS[key].title */
export const ONBOARDING_STEP_LABELS: Record<OnboardingStepKey, string> = {
  LEAGUE_SELECTED: ONBOARDING_STEP_DEFAULTS.LEAGUE_SELECTED.title,
  ACTIVE_CARD_SET: ONBOARDING_STEP_DEFAULTS.ACTIVE_CARD_SET.title,
  DAILY_QUIZ_DONE: ONBOARDING_STEP_DEFAULTS.DAILY_QUIZ_DONE.title,
  LOCATION_SCANNED: ONBOARDING_STEP_DEFAULTS.LOCATION_SCANNED.title,
  BATTLE_PLAYED: ONBOARDING_STEP_DEFAULTS.BATTLE_PLAYED.title,
}

export const ONBOARDING_STEP_ORDER: OnboardingStepKey[] = [
  "LEAGUE_SELECTED",
  "ACTIVE_CARD_SET",
  "DAILY_QUIZ_DONE",
  "LOCATION_SCANNED",
  "BATTLE_PLAYED",
]

export const DAILY_QUEST_LABELS: Record<
  DailyQuestKey,
  { title: string; description: string; rewardCoins: number; rewardChakra: number }
> = {
  DAILY_QUIZ: {
    title: "Пройти тест",
    description: "Ежедневный тест в академии",
    rewardCoins: 25,
    rewardChakra: 0,
  },
  WIN_BATTLE: {
    title: "Победа в дуэли",
    description: "Выиграй PvP-бой (радар, QR или вызов)",
    rewardCoins: 35,
    rewardChakra: 25,
  },
  SCAN_POINT: {
    title: "Скан точки",
    description: "Отсканируй точку на карте своего города",
    rewardCoins: 20,
    rewardChakra: 40,
  },
  WIN_TRAINING: {
    title: "Выиграй 2 тренировки",
    description: "Две победы в тренировочном зале",
    rewardCoins: 20,
    rewardChakra: 10,
  },
  CLAIM_TREASURE: {
    title: "Забери клад",
    description: "Забери клад на карте 1 раз в день",
    rewardCoins: 25,
    rewardChakra: 20,
  },
  PERFECT_QUIZ: {
    title: "Идеальный тест 3/3",
    description: "Ответь правильно на все 3 вопроса теста",
    rewardCoins: 30,
    rewardChakra: 15,
  },
}

export const WEEKLY_QUEST_LABELS: Record<
  WeeklyQuestKey,
  {
    title: string
    description: string
    target: number
    rewardCoins: number
    rewardChakra: number
  }
> = {
  WIN_5_BATTLES: {
    title: "5 PvP-побед",
    description: "Выиграй 5 боёв за неделю (радар, 1 на 1, вызов)",
    target: 5,
    rewardCoins: 100,
    rewardChakra: 75,
  },
  WIN_3_ELEMENTS: {
    title: "3 стихии",
    description: "Победи в PvP картами трёх разных стихий",
    target: 3,
    rewardCoins: 120,
    rewardChakra: 80,
  },
  DEFEAT_BOSS: {
    title: "Победить босса",
    description: "Одержи победу над боссом на карте",
    target: 1,
    rewardCoins: 80,
    rewardChakra: 60,
  },
  DEAL_WORLD_BOSS_DAMAGE: {
    title: "Урон мировому боссу",
    description: "Нанеси 5000 урона мировому боссу за неделю",
    target: 5000,
    rewardCoins: 100,
    rewardChakra: 75,
  },
  LEVEL_UP_CARDS: {
    title: "3 усиления карт",
    description: "Прокачай карты чакрой три раза",
    target: 3,
    rewardCoins: 80,
    rewardChakra: 40,
  },
  SUCCESSFUL_FUSE: {
    title: "Успешное слияние",
    description: "Успешно слей две карты",
    target: 1,
    rewardCoins: 100,
    rewardChakra: 50,
  },
  EARN_WAR_PTS: {
    title: "50 очков войны",
    description: "Набери 50 war-очков (скан, квиз, босс, PvP)",
    target: 50,
    rewardCoins: 120,
    rewardChakra: 60,
  },
  SCAN_30_POINTS: {
    title: "30 сканов точек",
    description: "Отсканируй 30 точек своего города за неделю",
    target: 30,
    rewardCoins: 200,
    rewardChakra: 200,
  },
}

export function formatShopReward(type: ShopRewardType, amount: number): string {
  if (type === "ENERGY") return `+${amount} энергии`
  if (type === "CHAKRA") return `+${amount} чакры`
  if (type === "COINS") return `+${amount} монет`
  if (type === "STYLE_CHANGE_TOKEN") return `+${amount} токен смены стиля`
  if (type === "FUSE_BOOSTER") return `+${amount} усилитель слияния`
  if (type === "SHARD_DUST") return `+${amount} пыли`
  if (type === "CARD_CAPACITY") return `+${amount} слотов рюкзака`
  if (type === "PROFILE_COSMETIC") return "Косметика карточки"
  return `+${amount}`
}

export function formatQuestReward(coins: number, chakra: number): string {
  const parts: string[] = []
  if (coins > 0) parts.push(`${coins} монет`)
  if (chakra > 0) parts.push(`${chakra} чакры`)
  return parts.join(" + ") || "—"
}
